// The current event and the reference data most screens need (divisions, teams, courts, court status).
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { collection, doc, orderBy, query } from 'firebase/firestore'
import {
  eventCol,
  eventDoc,
  SUB,
  type Court,
  type CourtStatus,
  type Division,
  type EventDoc,
  type Team,
  type WithId,
} from '@spark/shared'
import { db } from './firebase.ts'
import { EVENT_ID } from './env.ts'
import { useCollection, useDoc } from './hooks.ts'
import { useAuth } from './auth.tsx'

export interface EventState {
  eventId: string
  event: WithId<EventDoc> | null
  loading: boolean
  error: string | null
  timeZone: string
  /** Active courts in display order. */
  courts: Court[]
  allCourts: Court[]
  courtById: Map<string, Court>
  courtStatus: Map<string, WithId<CourtStatus>>
  divisions: WithId<Division>[]
  divisionById: Map<string, WithId<Division>>
  teams: WithId<Team>[]
  teamById: Map<string, WithId<Team>>
  teamName: (id: string | null | undefined, fallback?: string | null) => string
  divisionLabel: (id: string | null | undefined, short?: boolean) => string
  courtName: (id: string | null | undefined) => string
}

const Ctx = createContext<EventState | null>(null)

/** `loadTeams={false}` skips the all-teams listener (the scorekeeper console does not need it). */
export function EventProvider({ children, loadTeams = true }: { children: ReactNode; loadTeams?: boolean }) {
  const { access } = useAuth()
  const isStaff = !!access && loadTeams
  const eventState = useDoc<EventDoc>(doc(db(), eventDoc(EVENT_ID)), `event:${EVENT_ID}`)
  const divisionsState = useCollection<Division>(query(collection(db(), eventCol(EVENT_ID, SUB.divisions)), orderBy('sortOrder')), `divisions:${EVENT_ID}`)
  const teamsState = useCollection<Team>(isStaff ? query(collection(db(), eventCol(EVENT_ID, SUB.teams)), orderBy('name')) : null, `teams:${EVENT_ID}:${isStaff}`)
  const courtStatusState = useCollection<CourtStatus>(collection(db(), eventCol(EVENT_ID, SUB.courtStatus)), `courtStatus:${EVENT_ID}`)

  const event = eventState.data
  const allCourts = useMemo(() => [...(event?.courts ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [event])
  const courts = useMemo(() => allCourts.filter((c) => c.active), [allCourts])
  const courtById = useMemo(() => new Map(allCourts.map((c) => [c.id, c])), [allCourts])
  const divisions = divisionsState.data
  const divisionById = useMemo(() => new Map(divisions.map((d) => [d.id, d])), [divisions])
  const teams = teamsState.data
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams])
  const courtStatus = useMemo(() => new Map(courtStatusState.data.map((c) => [c.id, c])), [courtStatusState.data])

  const teamName = useCallback(
    (id: string | null | undefined, fallback?: string | null) => (id ? (teamById.get(id)?.name ?? fallback ?? 'Unknown team') : (fallback ?? 'To be decided')),
    [teamById],
  )
  const divisionLabel = useCallback(
    (id: string | null | undefined, short = false) => {
      const d = id ? divisionById.get(id) : undefined
      if (!d) return id ?? ''
      return short ? d.shortLabel || d.label : d.label
    },
    [divisionById],
  )
  const courtName = useCallback((id: string | null | undefined) => (id ? (courtById.get(id)?.name ?? id) : 'No court'), [courtById])

  const value: EventState = {
    eventId: EVENT_ID,
    event,
    loading: eventState.loading || divisionsState.loading,
    error: eventState.error?.message ?? divisionsState.error?.message ?? null,
    timeZone: event?.timezone ?? 'America/Indiana/Indianapolis',
    courts,
    allCourts,
    courtById,
    courtStatus,
    divisions,
    divisionById,
    teams,
    teamById,
    teamName,
    divisionLabel,
    courtName,
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useEvent(): EventState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useEvent outside EventProvider')
  return v
}
